import type { ReactNode } from 'react';
import { isMac } from '@/lib/keys';
import { openDialog, overlayStore } from '@/lib/overlays';
import { DialogBody, DialogDone, DialogFoot, DialogHead } from '@/ui/Dialog';
import { Icon } from '@/ui/Icon';
import { Kbds, KeysThen } from '@/ui/bits';

// The sheet of every keyboard shortcut, opened with ? or from the account menu.

const MOD = isMac ? '⌘' : 'Ctrl';
const SHIFT = isMac ? '⇧' : 'Shift';

const GROUPS: { title: string; rows: [string, ReactNode][] }[] = [
  {
    title: 'Go to',
    rows: [
      ['Search and commands', <Kbds keys={`${MOD} K`} />],
      ['Search', <Kbds keys="/" />],
      ['Home', <KeysThen first="G" then="T" />],
      ['Inbox', <KeysThen first="G" then="I" />],
      ['People', <KeysThen first="G" then="P" />],
      ['Companies', <KeysThen first="G" then="C" />],
      ['Deals', <KeysThen first="G" then="D" />],
      ['AI Finder', <KeysThen first="G" then="F" />],
      ['Activity', <KeysThen first="G" then="A" />],
    ],
  },
  {
    title: 'Lists, inbox and deals',
    rows: [
      ['Next row, conversation or deal', <Kbds keys="J" />],
      ['Previous row, conversation or deal', <Kbds keys="K" />],
      ['Select the row', <Kbds keys="X" />],
      ['Open it', <Kbds keys="↵" />],
      ['Edit a cell', <><span>Click or</span><Kbds keys="F2" /></>],
      ['Save the edit', <Kbds keys="↵" />],
      ['Cancel the edit', <Kbds keys="Esc" />],
      ['Clear the selection', <Kbds keys="Esc" />],
      ['Pick up a deal card', <Kbds keys="Space" />],
      ['Move it to another place or stage', <Kbds keys="← → ↑ ↓" />],
      ['Put it down', <Kbds keys="Space" />],
    ],
  },
  {
    title: 'Actions',
    rows: [
      ['Write an email', <Kbds keys="E" />],
      ['Reply, in Inbox and records', <Kbds keys="C" />],
      ['Reply in a conversation', <Kbds keys="R" />],
      ['Reply to everyone', <Kbds keys={`${SHIFT} R`} />],
      ['Send from the composer', <Kbds keys={`${MOD} ↵`} />],
      ['Minimise or reopen the composer', <Kbds keys={`${MOD} .`} />],
      ['Move between menu items', <Kbds keys="↑ ↓" />],
      ['This sheet', <Kbds keys="?" />],
      ['Close the top layer', <Kbds keys="Esc" />],
    ],
  },
];

export function shortcutsOpen(): boolean {
  return overlayStore.get().dialog?.label === 'Keyboard shortcuts';
}

export function openShortcuts(): void {
  openDialog({ label: 'Keyboard shortcuts', size: 'wide', render: (close) => <Shortcuts close={close} /> });
}

function Shortcuts({ close }: { close: () => void }) {
  return (
    <>
      <DialogHead
        title="Keyboard shortcuts"
        sub="Every screen works from the keyboard. Shortcuts pause while you type in a field."
        onClose={close}
      />
      <DialogBody>
        <div className="sc-grid">
          {GROUPS.map((g) => (
            <div className="sc-grp" key={g.title} role="group" aria-label={g.title}>
              <h3>{g.title}</h3>
              {g.rows.map(([label, keys]) => (
                <div className="sc-row" key={label}>
                  <span>{label}</span>
                  <span className="keys">{keys}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </DialogBody>
      <DialogFoot>
        <span className="note">
          <Icon name="keyboard" />
          Hover any button to see its key
        </span>
        <span className="spacer" />
        <DialogDone onClick={close} autoFocus />
      </DialogFoot>
    </>
  );
}
